import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  AlertTriangle, ArrowLeft, Check, ExternalLink, HelpCircle, Loader2, MapPin, MapPinOff, Search, X,
} from 'lucide-react';
import { useTreeStore } from '@/store/useTreeStore';
import {
  ladeOrte,
  loescheOrtszuordnung,
  loeseOrteAuf,
  setzeOrt,
  type Ortseintrag,
} from '@/api/orte.api';

// ─── Ortsliste ───────────────────────────────────────────────────────────────
//
// Ortsangaben in Stammbäumen sind Freitext aus mehreren Jahrhunderten und
// mehreren Bearbeitern. Ein Nachschlagedienst macht daraus Koordinaten – und
// zwar auch dann, wenn er den Ort gar nicht kennt. Er antwortet fast immer
// irgendetwas.
//
// Genau das ist die Falle: Ein Ort OHNE Treffer ist harmlos, man sieht, dass
// etwas fehlt. Ein FALSCHER Treffer sieht aus wie eine Antwort, und der Punkt
// sitzt in Brasilien statt in Thüringen.
//
// Diese Seite dreht das um. Sie zeigt zuerst, was verdächtig ist, dann was
// fehlt, dann den Rest – und lässt beides von Hand geradeziehen. Die
// Reihenfolge ist die Arbeitsreihenfolge: Wer 400 Orte hat, soll nicht suchen
// müssen, wo sich das Nachsehen lohnt.

const VERDACHT_TEXT: Record<string, string> = {
  fremdes_land: 'Anderes Land',
  zu_grob: 'Zu ungenau',
  unsicher: 'Unsicher',
};

function Merkmal({ eintrag }: { eintrag: Ortseintrag }) {
  if (eintrag.verdacht !== 'ok') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
        {VERDACHT_TEXT[eintrag.verdacht] ?? 'Prüfen'}
      </span>
    );
  }
  switch (eintrag.stand) {
    case 'nicht_gefunden':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
          <HelpCircle className="h-3 w-3" aria-hidden="true" />
          Nicht gefunden
        </span>
      );
    case 'offen':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
          Noch nicht gesucht
        </span>
      );
    case 'von_hand':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
          <Check className="h-3 w-3" aria-hidden="true" />
          Von Hand
        </span>
      );
    case 'ohne_karte':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
          <MapPinOff className="h-3 w-3" aria-hidden="true" />
          Nicht auf der Karte
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
          <Check className="h-3 w-3" aria-hidden="true" />
          Zugeordnet
        </span>
      );
  }
}

// ─── Einen Ort von Hand setzen ───────────────────────────────────────────────

function Korrektur({ eintrag, onFertig }: { eintrag: Ortseintrag; onFertig: () => void }) {
  const queryClient = useQueryClient();
  const [lat, setLat] = useState(eintrag.lat?.toString() ?? '');
  const [lon, setLon] = useState(eintrag.lon?.toString() ?? '');
  const [name, setName] = useState(eintrag.anzeigename ?? '');

  const neuladen = () => queryClient.invalidateQueries({ queryKey: ['orte'] });

  const speichern = useMutation({
    mutationFn: () =>
      setzeOrt(eintrag.schluessel, {
        lat: lat.trim() === '' ? null : Number(lat.replace(',', '.')),
        lon: lon.trim() === '' ? null : Number(lon.replace(',', '.')),
        anzeigename: name.trim() || null,
      }),
    onSuccess: () => {
      toast.success('Ort gesetzt.');
      neuladen();
      onFertig();
    },
    onError: (e: Error) => toast.error(e.message || 'Das hat nicht geklappt.'),
  });

  const vonKarteNehmen = useMutation({
    mutationFn: () => setzeOrt(eintrag.schluessel, { lat: null, lon: null, anzeigename: null }),
    onSuccess: () => {
      toast.success('Dieser Ort erscheint nicht mehr auf der Karte.');
      neuladen();
      onFertig();
    },
  });

  const zuruecknehmen = useMutation({
    mutationFn: () => loescheOrtszuordnung(eintrag.schluessel),
    onSuccess: () => {
      toast.success('Eigene Zuordnung zurückgenommen.');
      neuladen();
      onFertig();
    },
  });

  const gueltig =
    (lat.trim() === '' && lon.trim() === '') ||
    (!Number.isNaN(Number(lat.replace(',', '.'))) && !Number.isNaN(Number(lon.replace(',', '.'))));

  return (
    <div className="mt-3 space-y-3 rounded-md border border-blue-200 bg-blue-50/60 p-3">
      <p className="text-xs text-slate-600">
        Koordinaten heraussuchen und hier eintragen. Beide Felder leer lassen heißt: Dieser Ort
        gehört auf keine Karte – etwa „auf See" oder „im Feld".
      </p>

      <div className="flex flex-wrap gap-2">
        <label className="flex-1 text-xs text-slate-600">
          Breite
          <input
            value={lat}
            onChange={(e) => setLat(e.target.value)}
            placeholder="50.9787"
            inputMode="decimal"
            className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm text-slate-900"
          />
        </label>
        <label className="flex-1 text-xs text-slate-600">
          Länge
          <input
            value={lon}
            onChange={(e) => setLon(e.target.value)}
            placeholder="11.0328"
            inputMode="decimal"
            className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm text-slate-900"
          />
        </label>
        <label className="flex-[2] text-xs text-slate-600">
          Name auf der Karte
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={eintrag.rohtext}
            className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm text-slate-900"
          />
        </label>
      </div>

      {/* Der Weg zu den Koordinaten. Bewusst ein Link nach draußen und kein
          eingebautes Suchfeld: Die Karte dort zeigt gleich, ob der Ort der
          richtige ist – und genau darum geht es hier. */}
      <a
        href={`https://www.openstreetmap.org/search?query=${encodeURIComponent(eintrag.rohtext)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-xs text-blue-700 hover:underline"
      >
        <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        „{eintrag.rohtext}" auf OpenStreetMap suchen
      </a>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => speichern.mutate()}
          disabled={!gueltig || speichern.isPending}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          Übernehmen
        </button>
        <button
          onClick={() => vonKarteNehmen.mutate()}
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
        >
          Nicht auf die Karte
        </button>
        {(eintrag.stand === 'von_hand' || eintrag.stand === 'ohne_karte') && (
          <button
            onClick={() => zuruecknehmen.mutate()}
            className="rounded-md px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100"
          >
            Zurücknehmen
          </button>
        )}
        <button onClick={onFertig} className="rounded-md px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100">
          Schließen
        </button>
      </div>
    </div>
  );
}

// ─── Die Seite ───────────────────────────────────────────────────────────────

export function OrtePage() {
  const queryClient = useQueryClient();
  const [suche, setSuche] = useState('');
  const [nurArbeit, setNurArbeit] = useState(false);
  const [offen, setOffen] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fortschritt, setFortschritt] = useState<{ erledigt: number; offen: number } | null>(null);

  const { data: orte, isLoading } = useQuery({ queryKey: ['orte'], queryFn: ladeOrte });

  const zahlen = useMemo(() => {
    const a = { gesamt: 0, zugeordnet: 0, zuPruefen: 0, fehlt: 0, offen: 0 };
    for (const o of orte ?? []) {
      a.gesamt += 1;
      if (o.verdacht !== 'ok') a.zuPruefen += 1;
      else if (o.stand === 'nicht_gefunden') a.fehlt += 1;
      else if (o.stand === 'offen') a.offen += 1;
      else a.zugeordnet += 1;
    }
    return a;
  }, [orte]);

  const gefiltert = useMemo(() => {
    const suchtext = suche.trim().toLowerCase();
    return (orte ?? []).filter((o) => {
      if (nurArbeit && o.verdacht === 'ok' && o.stand !== 'nicht_gefunden' && o.stand !== 'offen') {
        return false;
      }
      if (suchtext === '') return true;
      return o.rohtext.toLowerCase().includes(suchtext)
        || (o.anzeigename?.toLowerCase().includes(suchtext) ?? false);
    });
  }, [orte, suche, nurArbeit]);

  /**
   * Auflösen in Häppchen.
   *
   * Der Dienst erlaubt eine Anfrage je Sekunde; 400 Orte dauern über sieben
   * Minuten. Statt einer HTTP-Anfrage, die so lange offen steht (und an jedem
   * Reverse-Proxy stirbt), holt die Seite Stapel für Stapel und zeigt dabei,
   * wie weit sie ist.
   */
  const alleAufloesen = async () => {
    setLaeuft(true);
    let erledigt = 0;
    try {
      for (;;) {
        const r = await loeseOrteAuf();
        erledigt += r.bearbeitet;
        setFortschritt({ erledigt, offen: r.offen });
        if (r.offen === 0 || r.bearbeitet === 0) break;
      }
      await queryClient.invalidateQueries({ queryKey: ['orte'] });
      toast.success(erledigt === 0 ? 'Es war nichts offen.' : `${erledigt} Orte nachgeschlagen.`);
    } catch (e) {
      toast.error((e as Error).message || 'Die Ortssuche hat abgebrochen.');
      await queryClient.invalidateQueries({ queryKey: ['orte'] });
    } finally {
      setLaeuft(false);
      setFortschritt(null);
    }
  };

  if (isLoading) {
    return <p className="p-8 text-center text-sm text-slate-400">Wird geladen …</p>;
  }

  return (
    <div className="mx-auto max-w-4xl space-y-5 p-4 sm:p-6">
      <div>
        {/* Der Weg zurück: die Seite wird von der Karte aus geöffnet und hatte
            bisher keinen Ausgang außer dem Menü. */}
        <Link
          to="/"
          onClick={() => useTreeStore.getState().setViewMode('map')}
          className="mb-2 inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:underline"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Zurück zur Karte
        </Link>
        <h1 className="text-xl font-semibold text-slate-900">Orte</h1>
        <p className="mt-1 text-sm text-slate-500">
          Jeder Ort aus Geburt, Tod, Eheschließung und Beruf – einmal nachgeschlagen und
          dauerhaft gespeichert. Was zweifelhaft aussieht, steht oben.
        </p>
      </div>

      {/* Zahlen */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: 'Orte insgesamt', wert: zahlen.gesamt, farbe: 'text-slate-900' },
          { label: 'Zu prüfen', wert: zahlen.zuPruefen, farbe: 'text-amber-700' },
          { label: 'Nicht gefunden', wert: zahlen.fehlt, farbe: 'text-red-700' },
          { label: 'Zugeordnet', wert: zahlen.zugeordnet, farbe: 'text-green-700' },
        ].map((k) => (
          <div key={k.label} className="rounded-lg border border-slate-200 bg-white p-3">
            <p className={`text-2xl font-semibold ${k.farbe}`}>{k.wert}</p>
            <p className="text-xs text-slate-500">{k.label}</p>
          </div>
        ))}
      </div>

      {zahlen.offen > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
          <p className="text-sm text-blue-900">
            {zahlen.offen === 1
              ? 'Ein Ort wurde noch nicht nachgeschlagen.'
              : `${zahlen.offen} Orte wurden noch nicht nachgeschlagen.`}{' '}
            <span className="text-blue-700">
              Der Dienst erlaubt eine Anfrage je Sekunde – das dauert entsprechend, aber nur
              dieses eine Mal.
            </span>
          </p>
          <button
            onClick={alleAufloesen}
            disabled={laeuft}
            className="inline-flex shrink-0 items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {laeuft && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {laeuft
              ? `${fortschritt?.erledigt ?? 0} von ${(fortschritt?.erledigt ?? 0) + (fortschritt?.offen ?? 0)}`
              : 'Jetzt nachschlagen'}
          </button>
        </div>
      )}

      {/* Filter */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <input
            type="search"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder="Ort suchen …"
            aria-label="Ort suchen"
            className="w-full rounded-md border border-slate-300 py-1.5 pl-8 pr-3 text-sm"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={nurArbeit}
            onChange={(e) => setNurArbeit(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300"
          />
          Nur, was Arbeit macht
        </label>
      </div>

      {/* Liste */}
      <div className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
        {gefiltert.length === 0 && (
          <p className="p-8 text-center text-sm text-slate-400">
            {zahlen.gesamt === 0
              ? 'In diesem Stammbaum steht noch kein Ort.'
              : 'Nichts gefunden.'}
          </p>
        )}
        {gefiltert.map((o) => (
          <div key={o.schluessel} className="p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-slate-900">{o.rohtext}</span>
                  <Merkmal eintrag={o} />
                  <span className="text-xs text-slate-400">{o.anzahl}×</span>
                </div>
                {/* Der gefundene Name ist die Probe aufs Exempel: Steht dort
                    „Erfurt, Thüringen", stimmt es. Steht dort „Erfurt, Iowa",
                    sieht man den Fehler sofort. */}
                {o.anzeigename && (
                  <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500">
                    <MapPin className="h-3 w-3 shrink-0" aria-hidden="true" />
                    <span className="truncate">{o.anzeigename}</span>
                  </p>
                )}
                {o.grund && <p className="mt-1 text-xs text-amber-700">{o.grund}</p>}
              </div>
              <button
                onClick={() => setOffen(offen === o.schluessel ? null : o.schluessel)}
                className="shrink-0 rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
              >
                {offen === o.schluessel ? <X className="h-4 w-4" aria-hidden="true" /> : 'Zuordnen'}
              </button>
            </div>
            {offen === o.schluessel && <Korrektur eintrag={o} onFertig={() => setOffen(null)} />}
          </div>
        ))}
      </div>
    </div>
  );
}
